/* ProductsCrudCard.tsx
	Une card affichant la gestion CRUD des produits
	Utilise le composant GenericCrudCard pour lister, ajouter, éditer et supprimer les éléments
	Utilise listPaginated pour bénéficier de la pagination, du tri et des filtres côté serveur
*/

// Import React
import React from 'react';

// Import FontAwesome
import { faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

// Import Communs
import { GenericCrudCard } from 'common';

// Import Feature
import { ProductData, productsApi } from 'features/products';

// Interface des props du composant
interface ProductsCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
}

// ProductsCrudCard component
export const ProductsCrudCard: React.FC<ProductsCrudCardProps> = ({
	size = 'sm',
	returnPath = '/app/settings',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [{ label: 'ID', field: 'id', render: (a: ProductData) => a.id }]
			: []),
		// Colonne "Label"
		{
			label: 'Label',
			field: 'label',
			render: (a: ProductData) => a.label,
		},
		// Colonne Description
		{
			label: 'Description',
			field: 'description',
			render: (a: ProductData) => a.description ?? '—',
		},
		// Colonne "SKU"
		{
			label: 'SKU',
			field: 'sku',
			render: (a: ProductData) => a.sku ?? '—',
		},
		// Colonne "Prix défaut" (tri désactivé : champ calculé / formaté)
		{
			label: 'Prix défaut',
			field: 'defaultPrice',
			render: (a: ProductData) =>
				a.defaultPrice !== undefined ? `${a.defaultPrice.toFixed(2)} €` : '—',
		},
		// Colonne "Taux de taxe par défaut"
		{
			label: 'Taux de taxe',
			field: 'none',
			render: (a: ProductData) => a.defaultTaxRate?.label ?? '—',
		},
		// Colonne "A/V" : indique si le produit est pour l'achat et/ou la vente
		{
			label: 'A/V',
			field: 'none',
			render: (a: ProductData) =>
				(a.forPurchase ? 'A' : '') + (a.forSale ? 'V' : '') || '—',
		},
	];

	return (
		<GenericCrudCard<ProductData>
			queryKey="products"
			title={size === 'sm' ? 'Produits' : 'Liste des produits'}
			fetchData={(token, options) => productsApi.listPaginated(token, options)}
			deleteItem={productsApi.delete}
			tableColumns={columns}
			addButtonLabel="Ajouter un produit"
			getAddPath={() => '/app/settings/products/new'}
			getEditPath={(id) => `/app/settings/products/${id}/edit`}
			emptyMessage="Aucun produit trouvé."
			getReturnPath={() => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' ? faLayerGroup : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};

export default ProductsCrudCard;
